// AdminShell — the NEXOVA admin/ops chrome.
//
// 256px grouped sidebar (Finance / Operations / Relationships / Treasury / Data)
// with live queue-count badges on the two request queues, and a sticky dark
// topbar (page title + search ⌘K hint + theme toggle + notification bell + OP
// avatar). DARK by default (ThemeProvider). Auth/protection is unchanged — this
// shell only renders inside ProtectedRoute, and Log out still best-effort calls
// /admin/logout then clears the in-memory token and routes to /login.
//
// SECURITY: nothing in this shell relaxes role-gating; the backend enforces the
// admin role on every /admin/* call regardless of what the sidebar shows.

import { useEffect, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  Bell,
  BookOpen,
  Coins,
  CreditCard,
  Database,
  Handshake,
  Inbox,
  KeyRound,
  Layers,
  Scale,
  LayoutDashboard,
  LogOut,
  ScrollText,
  Search,
  Send,
  TrendingUp,
  Users,
  UsersRound,
  Wallet,
} from "lucide-react";

import { api } from "@/api/client";
import { clearAccessToken } from "@/lib/auth";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { ThemeToggle } from "./ThemeToggle";

interface NavItem {
  to: string;
  label: string;
  icon: React.ComponentType<{ className?: string }>;
  // Badge source key (queue counts), if this item shows a count.
  badge?: "account_requests" | "recharge_requests";
}

interface NavGroup {
  label: string;
  items: NavItem[];
}

const NAV: NavGroup[] = [
  {
    label: "Finance",
    items: [
      { to: "/ledger", label: "Master ledger", icon: BookOpen },
      { to: "/pnl", label: "P&L summary", icon: TrendingUp },
      { to: "/reconciliation", label: "Reconciliation", icon: Scale },
    ],
  },
  {
    label: "Operations",
    items: [
      {
        to: "/account-requests",
        label: "Account queue",
        icon: Inbox,
        badge: "account_requests",
      },
      {
        to: "/recharge-requests",
        label: "Recharge queue",
        icon: CreditCard,
        badge: "recharge_requests",
      },
      { to: "/ops-bot", label: "Ops bot", icon: Send },
    ],
  },
  {
    label: "Relationships",
    items: [{ to: "/crm", label: "CRM", icon: Users }],
  },
  {
    label: "Treasury",
    items: [{ to: "/wallets", label: "Wallet provisioning", icon: Wallet }],
  },
  {
    label: "Data",
    items: [
      { to: "/import", label: "Seed import", icon: Database },
      { to: "/reporting-keys", label: "Reporting keys", icon: KeyRound },
    ],
  },
  {
    label: "Platform",
    items: [
      { to: "/accounts", label: "Accounts", icon: Layers },
      { to: "/agencies", label: "Agencies", icon: Handshake },
      { to: "/clients", label: "Clients", icon: UsersRound },
      { to: "/audit", label: "Audit", icon: ScrollText },
      { to: "/dashboard", label: "Ads BC dashboard", icon: LayoutDashboard },
    ],
  },
];

// GET /admin/queues/counts (admin JWT, NoStore) — counts of ACTIONABLE queue
// items: account requests in pending|assigned|in_progress|ready, recharge
// requests in requested|requested_to_agency|topped_up. Read-only, badge-only.
interface QueueCounts {
  account_requests: number;
  recharge_requests: number;
}

const NO_COUNTS: QueueCounts = { account_requests: 0, recharge_requests: 0 };

// Coerce ONE badge count out of whatever the endpoint returned. A missing key,
// a null body, a string, or a negative number all degrade to 0 rather than
// rendering NaN or crashing the shell — the badge is decoration, the queue
// pages are the source of truth.
function badgeCount(v: unknown): number {
  return typeof v === "number" && Number.isFinite(v) && v > 0 ? Math.floor(v) : 0;
}

export function parseQueueCounts(body: unknown): QueueCounts {
  const b = (body ?? {}) as Partial<Record<keyof QueueCounts, unknown>>;
  return {
    account_requests: badgeCount(b.account_requests),
    recharge_requests: badgeCount(b.recharge_requests),
  };
}

// Page title resolved from the active route, for the topbar.
const TITLES: Record<string, string> = {
  "/ledger": "Master ledger",
  "/pnl": "P&L summary",
  "/reconciliation": "Reconciliation",
  "/account-requests": "Account-request queue",
  "/recharge-requests": "Recharge-request queue",
  "/ops-bot": "Ops bot",
  "/crm": "CRM",
  "/wallets": "Wallet provisioning",
  "/import": "Seed import",
  "/reporting-keys": "Reporting keys",
  "/accounts": "Accounts",
  "/agencies": "Agencies",
  "/clients": "Clients",
  "/audit": "Audit",
  "/dashboard": "Ads BC dashboard",
};

function titleForPath(pathname: string): string {
  const match = Object.keys(TITLES).find((p) => pathname.startsWith(p));
  return match ? TITLES[match] : "Admin · Ops";
}

export function AdminShell({ children }: { children: React.ReactNode }) {
  const navigate = useNavigate();
  const [counts, setCounts] = useState<QueueCounts>(NO_COUNTS);

  useEffect(() => {
    // Queue badges are BEST-EFFORT: a 404 (route not deployed yet), a 5xx, or a
    // malformed body must degrade to no badges — silently. No toast: the shell
    // renders on every screen, so a noisy failure here would spam every page.
    api
      .get<unknown>("/admin/queues/counts")
      .then((body) => setCounts(parseQueueCounts(body)))
      .catch(() => setCounts(NO_COUNTS));
  }, []);

  async function logout() {
    await api.post("/admin/logout").catch(() => {});
    clearAccessToken();
    navigate("/login");
  }

  return (
    <div className="flex min-h-screen bg-nx-bg text-nx-text">
      {/* Sidebar */}
      <aside className="sticky top-0 hidden h-screen w-64 shrink-0 flex-col border-r border-nx-border bg-nx-surface md:flex">
        <div className="flex h-15 items-center gap-2 border-b border-nx-border px-4">
          <span className="grid size-7 place-items-center rounded-md bg-nx-brand text-nx-brand-fg">
            <Coins className="size-4" />
          </span>
          <div className="flex flex-col leading-none">
            <span className="text-sm font-semibold">NEXOVA</span>
            <span className="text-[0.7rem] text-nx-text-3">Admin · Ops</span>
          </div>
        </div>
        <nav
          aria-label="Primary"
          className="flex-1 space-y-5 overflow-y-auto px-3 py-4"
        >
          {NAV.map((group) => (
            <div key={group.label}>
              <p className="px-2 pb-1 text-[0.68rem] font-semibold tracking-wider text-nx-text-3 uppercase">
                {group.label}
              </p>
              <ul className="space-y-0.5">
                {group.items.map((item) => {
                  const count = item.badge ? counts[item.badge] : 0;
                  const Icon = item.icon;
                  return (
                    <li key={item.to}>
                      <NavLink
                        to={item.to}
                        className={({ isActive }) =>
                          cn(
                            "flex items-center gap-2.5 rounded-md px-2 py-1.5 text-sm text-nx-text-2 transition-colors hover:bg-nx-surface-2 hover:text-nx-text",
                            isActive &&
                              "bg-nx-brand-soft font-medium text-nx-brand hover:bg-nx-brand-soft hover:text-nx-brand",
                          )
                        }
                      >
                        <Icon className="size-4 shrink-0" />
                        <span className="flex-1 truncate">{item.label}</span>
                        {item.badge && count > 0 && (
                          <span className="grid size-5 place-items-center rounded-full bg-nx-debit-soft text-[0.7rem] font-semibold text-nx-debit tabular-nums">
                            {count}
                          </span>
                        )}
                      </NavLink>
                    </li>
                  );
                })}
              </ul>
            </div>
          ))}
        </nav>
      </aside>

      {/* Main column */}
      <div className="flex min-w-0 flex-1 flex-col">
        <header className="sticky top-0 z-30 flex h-15 items-center gap-3 border-b border-nx-border bg-nx-surface/95 px-4 backdrop-blur supports-backdrop-filter:bg-nx-surface/80">
          <h1 className="font-heading text-base font-medium">
            <RouteTitle />
          </h1>
          <button
            type="button"
            className="ml-auto hidden items-center gap-2 rounded-md border border-nx-border bg-nx-bg-2 px-2.5 py-1.5 text-xs text-nx-text-3 transition-colors hover:text-nx-text-2 sm:flex"
          >
            <Search className="size-3.5" />
            <span>Search</span>
            <kbd className="rounded bg-nx-surface-2 px-1 font-mono text-[0.65rem]">
              ⌘K
            </kbd>
          </button>
          <div className="ml-auto flex items-center gap-1 sm:ml-0">
            <ThemeToggle />
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Notifications"
              className="relative"
            >
              <Bell />
              <span className="absolute top-1 right-1 size-1.5 rounded-full bg-nx-debit" />
            </Button>
            <span
              aria-label="Operator"
              title="Operator"
              className="grid size-7 place-items-center rounded-full bg-nx-elevated text-xs font-semibold text-nx-text-2"
            >
              OP
            </span>
            <Button
              variant="ghost"
              size="icon-sm"
              aria-label="Log out"
              onClick={logout}
            >
              <LogOut />
            </Button>
          </div>
        </header>

        <main className="min-w-0 flex-1 px-5 py-6">{children}</main>
      </div>
    </div>
  );
}

// Reads the active location for the topbar title.
function RouteTitle() {
  const { pathname } = useLocation();
  return <>{titleForPath(pathname)}</>;
}
